// Stocky の仕入税区分には検証済みの公式 CSV export が無いため、利用者が作った
// 小さな表だけを受ける fallback。旧 backend へunsafeな別URLでfallbackせず、
// 新 SPA × 旧 backend の配信窓はこの専用URLの404で安全に停止する。
import { apiFetch } from "./client";

export { fileToBase64 } from "./reorder_policy_import";

const MANUAL_PURCHASE_TAX_TYPES =
  "/api/migrate/csv/purchase-tax-types-manual";

export interface ManualTaxTypeInvalidRow {
  row: number;
  reason: string;
}

export interface ManualTaxTypePreview {
  headers: string[];
  sample_rows: string[][];
  total_rows: number;
  guessed_mapping: Record<string, string | null>;
}

export interface ManualTaxTypeMapping {
  name: string;
  rate_percent: string;
  stocky_tax_type_id?: string | null;
  purpose?: string | null;
}

export interface ManualTaxTypeImportResult {
  total_rows: number;
  valid_rows: number;
  created: number;
  updated: number;
  unchanged: number;
  /** dry-runで、確定時に新規作成されるShopTaxRate件数。 */
  tax_rates_to_create: number;
  tax_rates_created: number;
  invalid_rows: ManualTaxTypeInvalidRow[];
  invalid_row_count: number;
  dry_run: boolean;
  /** dry-runだけ非null。exact input/tenant/DB write planをHMACで束縛する。 */
  plan_token: string | null;
}

export type ManualTaxTypeSource =
  | {
      kind: "csv_file";
      fileBase64: string;
      sourceName?: string;
    }
  | {
      kind: "pasted_table";
      pastedText: string;
      sourceName?: string;
    };

interface ApplyOptions {
  dryRun: boolean;
  purchaseOnlyAttested: boolean;
  expectedPlanToken?: string;
  sourceImportId: string;
  sourceFileName: string;
  sourceMediaType: string;
}

function sourceBody(source: ManualTaxTypeSource): Record<string, unknown> {
  const sourceName =
    source.sourceName === undefined ? {} : { source_name: source.sourceName };
  if (source.kind === "csv_file") {
    return { file_base64: source.fileBase64, ...sourceName };
  }
  return { pasted_text: source.pastedText, ...sourceName };
}

/** 空の任意列を送らない。旧/別semanticのAPIへfallbackもしない。 */
function mappingBody(
  mapping: ManualTaxTypeMapping,
): Record<string, string> {
  return {
    name: mapping.name,
    rate_percent: mapping.rate_percent,
    ...(mapping.stocky_tax_type_id
      ? { stocky_tax_type_id: mapping.stocky_tax_type_id }
      : {}),
    ...(mapping.purpose ? { purpose: mapping.purpose } : {}),
  };
}

export function previewManualPurchaseTaxTypes(
  source: ManualTaxTypeSource,
): Promise<ManualTaxTypePreview> {
  return apiFetch<ManualTaxTypePreview>(
    `${MANUAL_PURCHASE_TAX_TYPES}/preview`,
    {
      method: "POST",
      body: sourceBody(source),
    },
  );
}

export function applyManualPurchaseTaxTypes(
  source: ManualTaxTypeSource,
  mapping: ManualTaxTypeMapping,
  options: ApplyOptions,
): Promise<ManualTaxTypeImportResult> {
  return apiFetch<ManualTaxTypeImportResult>(
    `${MANUAL_PURCHASE_TAX_TYPES}/apply`,
    {
      method: "POST",
      body: {
        ...sourceBody(source),
        mapping: mappingBody(mapping),
        purchase_only_attested: options.purchaseOnlyAttested,
        dry_run: options.dryRun,
        source_import_id: options.sourceImportId,
        source_file_name: options.sourceFileName,
        source_media_type: options.sourceMediaType,
        ...(options.expectedPlanToken === undefined
          ? {}
          : { expected_plan_token: options.expectedPlanToken }),
      },
    },
  );
}
